import { useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { Download, Edit, Eye } from 'lucide-react'
import { Button, Chip } from 'vnetwork-ui'
import SubHeaders from '@/components/Headers/SubHeaders'
import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    createColumns,
    createCurrencyFormatter,
    createDateFormatter,
} from '@/components/ui/data-table-customized'
import type {
    DataTableAction,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useFilePreviewWithPresigned } from '@/hooks/useFilePreviewWithPresigned'
import { useModal } from '@/contexts/modal'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { formatCurrency } from '@/utils/function'
import { generatePathDatacentral, paths } from '@/utils/path'
import { getSupplier, getSupplierCustomerContracts } from '../../services'
import type { PartnerContract, SupplierCustomerContract } from '../../types'
import { SUPPLIER_STATUS_OPTIONS } from '../../utils/suppliers-config'
import { CONTRACT_STATUS } from '@/modules/Contract/constants'
import { ContractStatus } from '@/modules/Contract/types'

const SupplierDetail = () => {
    const { t } = useTranslation('contract')
    const navigate = useNavigate()
    const { partnerID } = useParams<{ partnerID: string }>()
    const { setModal } = useModal()
    const { openFilePreview, downloadFileWithPresigned } =
        useFilePreviewWithPresigned()

    const { data: supplierData, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SUPPLIER, Number(partnerID)],
        queryFn: () => getSupplier(Number(partnerID)),
        enabled: !!partnerID,
    })

    const { data: customerContractsData } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.SUPPLIER, Number(partnerID), 'contracts'],
            queryFn: () => getSupplierCustomerContracts(Number(partnerID)),
            enabled: !!partnerID,
        },
        { showError: false, showLoading: false }
    )

    // All hooks must be called before any early return
    const handlePreview = useCallback(
        async (row: PartnerContract) => {
            const attachments = row?.attachments?.filter(
                (a) => !a?.attachment_closed
            )
            if (attachments?.length === 1) {
                await openFilePreview(attachments[0])
            } else if (attachments?.length > 1) {
                toast.error(t('has-more-one-attachment'))
            } else {
                toast.error(t('no-attachment'))
            }
        },
        [openFilePreview, t]
    )

    const handleDownload = useCallback(
        async (row: PartnerContract) => {
            const attachments = row?.attachments?.filter(
                (a) => !a?.attachment_closed
            )
            if (attachments?.length === 1) {
                await downloadFileWithPresigned(attachments[0])
            } else if (attachments?.length > 1) {
                toast.error(t('has-more-one-attachment'))
            } else {
                toast.error(t('no-attachment'))
            }
        },
        [downloadFileWithPresigned, t]
    )

    // Early returns after all hooks
    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const supplier = supplierData?.result
    if (!supplier) return null

    const statusOption = SUPPLIER_STATUS_OPTIONS(t).find(
        (s) => s.value === supplier.partner_status
    )

    const contracts = supplier.contracts || []
    const customerContracts = customerContractsData?.results || []

    const contractColumns: DataTableColumn<PartnerContract>[] = createColumns([
        {
            key: 'contract_number_id',
            title: t('contract_number_id'),
            width: 200,
            cellClassName: 'font-medium text-primary',
        },
        {
            key: 'contract_key',
            title: t('contract_key'),
            width: 200,
        },
        {
            key: 'contract_status',
            title: t('contract_status'),
            width: 150,
            render: (value) => {
                const status = CONTRACT_STATUS(t).find((s) => s.value === value)
                return status ? (
                    <Chip
                        label={status.label}
                        size="small"
                        variant="contained"
                        color={status.color as any}
                    />
                ) : (
                    (value as string) || '-'
                )
            },
        },
        {
            key: 'contract_value',
            title: t('contract_value'),
            width: 160,
            cellClassName: 'text-right',
            formatter: createCurrencyFormatter({ currency: 'đ' }),
        },
        {
            key: 'contract_effective_date',
            title: t('contract_effective_date'),
            width: 140,
            cellClassName: 'text-right',
            formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
        },
        {
            key: 'contract_expiry_date',
            title: t('contract_expiry_date'),
            width: 140,
            cellClassName: 'text-right',
            formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
        },
    ])

    const contractActions: DataTableAction<PartnerContract>[] = [
        {
            key: 'preview',
            label: t('contract-preview'),
            icon: <Eye className="w-4 h-4" />,
            disabled: (row) => row?.contract_status !== ContractStatus.Active,
            onClick: handlePreview,
        },
        {
            key: 'download',
            label: t('download-file'),
            icon: <Download className="w-4 h-4" />,
            disabled: (row) => row?.contract_status !== ContractStatus.Active,
            onClick: handleDownload,
        },
    ]

    const customerContractColumns: DataTableColumn<SupplierCustomerContract>[] =
        createColumns([
            {
                key: 'contract_number_id',
                title: t('contract_number_id'),
                width: 200,
                cellClassName: 'font-medium text-primary',
            },
            {
                key: 'customer_name',
                title: t('contract_owner'),
                width: 220,
                render: (_, row) =>
                    row.company_name || row.customer_name || '-',
            },
            {
                key: 'contract_status',
                title: t('contract_status'),
                width: 150,
                render: (value) => {
                    const status = CONTRACT_STATUS(t).find(
                        (s) => s.value === value
                    )
                    return status ? (
                        <Chip
                            label={status.label}
                            size="small"
                            variant="contained"
                            color={status.color as any}
                        />
                    ) : (
                        (value as string) || '-'
                    )
                },
            },
            {
                key: 'services',
                title: t('contract_service'),
                width: 260,
                render: (_, row) => {
                    const services = row.services || []
                    if (!services.length) return '-'
                    if (services.length === 1) {
                        return services[0].service_name
                    }
                    return `${services.length} ${t('services_count')}`
                },
            },
            {
                key: 'contract_value',
                title: t('contract_value'),
                width: 160,
                cellClassName: 'text-right',
                formatter: createCurrencyFormatter({ currency: 'đ' }),
            },
            {
                key: 'contract_effective_date',
                title: t('contract_effective_date'),
                width: 140,
                cellClassName: 'text-right',
                formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
            },
            {
                key: 'contract_expiry_date',
                title: t('contract_expiry_date'),
                width: 140,
                cellClassName: 'text-right',
                formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
            },
        ])

    return (
        <div className="space-y-2">
            <SubHeaders
                breadcrumbs={[
                    {
                        path: paths.partnerContractList,
                        title: t('contract-partners'),
                    },
                    {
                        path: '',
                        title: supplier.partner_name,
                    },
                ]}
                components={[
                    <Button
                        key="edit"
                        variant="outlined"
                        size="small"
                        onClick={() =>
                            setModal('ContractModals/SupplierModal/index', {
                                supplier,
                                mode: 'update',
                            })
                        }
                    >
                        <Edit className="w-4 h-4 mr-1" />
                        {t('edit')}
                    </Button>,
                ]}
            />

            {/* Supplier Information */}
            <Paper className="p-4">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-lg">
                            {supplier.partner_name?.charAt(0) || 'S'}
                        </div>
                        <div>
                            <h2 className="text-base font-semibold text-gray-900">
                                {supplier.partner_name}
                            </h2>
                            <span className="text-xs text-gray-500">
                                {supplier.partner_code}
                            </span>
                        </div>
                    </div>
                    <Chip
                        label={statusOption?.label || supplier.partner_status}
                        size="small"
                        variant="contained"
                        color={statusOption?.color as any}
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Contact */}
                    <div className="space-y-2.5">
                        <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                            {t('contact_info')}
                        </h3>
                        <InfoRow
                            label={t('partner_email')}
                            value={supplier.partner_email}
                        />
                        <InfoRow
                            label={t('partner_phone')}
                            value={supplier.partner_phone}
                        />
                        <InfoRow
                            label={t('partner_tax_code')}
                            value={supplier.partner_tax_code}
                        />
                    </div>

                    {/* Representative */}
                    <div className="space-y-2.5">
                        <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                            {t('partner_representative')}
                        </h3>
                        <InfoRow
                            label={t('partner_representative')}
                            value={supplier.partner_representative}
                        />
                        <InfoRow
                            label={t('partner_representative_phone')}
                            value={supplier.partner_representative_phone}
                        />
                        <InfoRow
                            label={t('manager')}
                            value={supplier.employee_name}
                        />
                    </div>

                    {/* Banking */}
                    <div className="space-y-2.5">
                        <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                            {t('bank_info')}
                        </h3>
                        <InfoRow
                            label={t('partner_bank_account')}
                            value={supplier.partner_bank_account}
                        />
                        <InfoRow
                            label={t('partner_bank_name')}
                            value={supplier.partner_bank_name}
                        />
                    </div>
                </div>

                {/* Note */}
                {supplier.partner_note && (
                    <div className="mt-4 pt-3 border-t border-gray-100">
                        <span className="text-xs text-gray-400">
                            {t('partner_note')}
                        </span>
                        <p className="text-sm text-gray-600 mt-0.5">
                            {supplier.partner_note}
                        </p>
                    </div>
                )}

                {/* Summary stats */}
                <div className="mt-4 pt-3 border-t border-gray-100 flex gap-6">
                    <div>
                        <span className="text-xs text-gray-400">
                            {t('total_contracts')}
                        </span>
                        <p className="text-lg font-semibold text-primary">
                            {supplier.total_contracts || 0}
                        </p>
                    </div>
                    <div>
                        <span className="text-xs text-gray-400">
                            {t('total_contract_value')}
                        </span>
                        <p className="text-lg font-semibold text-primary">
                            {formatCurrency(supplier.total_contract_value || 0)}
                        </p>
                    </div>
                </div>
            </Paper>

            {/* Linked Customer Contracts Table */}
            <Paper>
                <div className="px-4 pt-4 pb-2">
                    <h3 className="text-sm font-semibold text-gray-900">
                        {t('customer_contracts')}
                    </h3>
                </div>
                <DataTableCustomized<SupplierCustomerContract>
                    columns={customerContractColumns}
                    rows={customerContracts}
                    rowKey={(row) => String(row.contract_id)}
                    size="small"
                    resizable
                    emptyMessage={t('no-data')}
                    onRowClick={(row) =>
                        navigate(
                            generatePathDatacentral(paths.contractDetail, {
                                id: row.contract_id,
                            })
                        )
                    }
                    toolbar={{
                        settings: {
                            show: true,
                            storageKey: 'supplier-detail-customer-contracts',
                        },
                    }}
                />
            </Paper>

            {/* Contracts Table */}
            <Paper>
                <div className="px-4 pt-4 pb-2">
                    <h3 className="text-sm font-semibold text-gray-900">
                        {t('partner-contracts')}
                    </h3>
                </div>
                <DataTableCustomized<PartnerContract>
                    columns={contractColumns}
                    rows={contracts}
                    rowKey={(row) => String(row.contract_id)}
                    size="small"
                    resizable
                    actions={contractActions}
                    emptyMessage={t('no-data')}
                    onRowClick={(row) =>
                        navigate(`/contracts/partners/${row.contract_id}`)
                    }
                    toolbar={{
                        settings: {
                            show: true,
                            storageKey: 'supplier-detail-contracts',
                        },
                    }}
                />
            </Paper>
        </div>
    )
}

const InfoRow = ({
    label,
    value,
}: {
    label: string
    value?: string | null
}) => (
    <div>
        <span className="text-xs text-gray-400">{label}</span>
        <p className={`text-sm ${value ? 'text-gray-800' : 'text-gray-400'}`}>
            {value || '---'}
        </p>
    </div>
)

export default SupplierDetail
